import { logToExternalErrorHandlers, normalizePathname } from '@luminovo/commons';
import { useHistoryState } from '@luminovo/design-system';
import React, { type JSX } from 'react';

import { ResourceNotFound } from '@/shared/ui/NotFoundCard';
import { PageLayout } from '@/shared/ui/PageLayout';

/** Decides what the page logs: a missing route, or an existing route linked without a required query param. */
type NotFoundReason = 'unknown-url' | 'missing-search-param';

const NotFoundPage: React.FunctionComponent<{ reason?: NotFoundReason }> = ({
    reason = 'unknown-url',
}): JSX.Element => {
    const widgetNavigation = useHistoryState((state) => state.widgetNavigation);
    // As the page arrived: while a navigation away is pending, this page stays mounted and the history
    // state is already the next page's, which would report that page's URL.
    const [sentByWidget] = React.useState(widgetNavigation !== undefined);

    React.useEffect(() => {
        const normalizedPath = normalizePathname();
        if (sentByWidget || normalizedPath.startsWith('/apps/')) {
            return;
        }
        if (reason === 'missing-search-param') {
            logToExternalErrorHandlers(new Error(`Missing required query param: ${normalizedPath}`), {
                fingerprint: ['missing-search-param', normalizedPath],
            });
            return;
        }
        logToExternalErrorHandlers(new Error(`Page not found: ${normalizedPath}`), { fingerprint: [normalizedPath] });
    }, [sentByWidget, reason]);

    return (
        <PageLayout layout="centered">
            <ResourceNotFound />
        </PageLayout>
    );
};

export default NotFoundPage;
